import React, { useEffect, useState } from 'react';
import { getInvestorProfile, updateInvestorProfile, fetchApi } from '../../api';
import { useToast } from '../../components/Toast';

const SECTEUR_OPTIONS = ['Fintech', 'Insurtech', 'Regtech', 'Biotech', 'Greentech', 'Medtech', 'SaaS', 'HealthTech', 'Immotech'];
const STAGE_OPTIONS = ['Pré-amorçage', 'Amorçage', 'Série A', 'Série B', 'Série C+'];

export default function InvestorProfile() {
  const toast = useToast();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [lookupResult, setLookupResult] = useState(null);

  useEffect(() => {
    getInvestorProfile()
      .then(data => {
        setForm({
          first_name: data.first_name || '',
          last_name: data.last_name || '',
          email: data.email || '',
          company: data.company || '',
          phone: data.phone || '',
          investment_thesis: data.investment_thesis || '',
          preferred_sectors: data.preferred_sectors || '',
          preferred_stages: data.preferred_stages || '',
          min_note: data.min_note || '',
          referral_source: data.referral_source || '',
          consent_visible: data.consent_visible !== false,
          siret: data.siret || '',
          siren: data.siren || '',
          naf: data.naf || '',
          adresse: data.adresse || '',
          forme_juridique: data.forme_juridique || '',
          dirigeants: data.dirigeants || '',
          effectif: data.effectif || '',
          annee_creation: data.annee_creation || '',
        });
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const upd = (key, val) => { setForm(f => ({ ...f, [key]: val })); setSaved(false); };

  const handleSiretLookup = async () => {
    const siret = (form.siret || '').replace(/[^0-9]/g, '');
    if (siret.length < 9) { toast.error('Le SIRET doit contenir au moins 9 chiffres'); return; }
    setLookingUp(true);
    setLookupResult(null);
    try {
      const res = await fetchApi(`/siret/search?q=${siret.substring(0, 9)}`);
      if (res.results && res.results.length > 0) {
        const match = res.results.find(r => r.siret === siret) || res.results[0];
        setLookupResult(match);
        if (match.siren) upd('siren', match.siren);
        if (match.adresse && !form.adresse) upd('adresse', match.adresse);
        if (match.activite_principale) upd('naf', match.activite_principale);
        if (match.forme_juridique) upd('forme_juridique', match.forme_juridique);
        if (match.date_creation) {
          const year = parseInt(match.date_creation.substring(0, 4));
          if (year > 1900 && !form.annee_creation) upd('annee_creation', year);
        }
        if (match.dirigeants && match.dirigeants.length > 0 && !form.dirigeants) {
          upd('dirigeants', match.dirigeants.map(d => `${d.prenoms || ''} ${d.nom || ''} (${d.qualite || ''})`).join(', '));
        }
        if (match.tranche_effectif && !form.effectif) {
          const tranches = {'0':0,'1-2':2,'3-5':4,'6-9':8,'10-19':15,'20-49':35,'50-99':75,'100-199':150,'200-249':225,'250-499':375,'500-999':750};
          const eff = tranches[match.tranche_effectif];
          if (eff) upd('effectif', eff);
        }
      } else {
        setLookupResult({ not_found: true });
      }
    } catch (err) { toast.error('Erreur de recherche : ' + err.message); }
    setLookingUp(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateInvestorProfile(form);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      toast.error('Erreur: ' + err.message);
    }
    setSaving(false);
  };

  if (loading) return <div className="loading">Chargement du profil...</div>;
  if (!form) return <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-light)' }}>Profil non disponible.</div>;

  return (
    <div style={{ padding: '32px 0' }}>
      <div className="container" style={{ maxWidth: 800 }}>
        <h1 style={{ marginBottom: 8 }}>Mon Profil Investisseur</h1>
        <p style={{ color: 'var(--text-light)', marginBottom: 32, fontSize: 14 }}>
          Modifiez vos informations personnelles et vos préférences d'investissement.
        </p>

        <form onSubmit={handleSave}>
          {/* Informations personnelles */}
          <div className="card" style={{ marginBottom: 24 }}>
            <h3 style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 20 }}>👤</span> Informations personnelles
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label className="form-label">Prénom</label>
                <input type="text" value={form.first_name} onChange={e => upd('first_name', e.target.value)}
                  className="form-input" required />
              </div>
              <div>
                <label className="form-label">Nom</label>
                <input type="text" value={form.last_name} onChange={e => upd('last_name', e.target.value)}
                  className="form-input" required />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label className="form-label">Email</label>
                <input type="email" value={form.email} className="form-input" disabled
                  style={{ background: '#F5F5F5', color: 'var(--text-light)' }} />
                <span style={{ fontSize: 11, color: 'var(--text-light)' }}>L'email ne peut pas être modifié</span>
              </div>
              <div>
                <label className="form-label">Téléphone</label>
                <input type="tel" value={form.phone} onChange={e => upd('phone', e.target.value)}
                  className="form-input" placeholder="+33 6 00 00 00 00" />
              </div>
            </div>
            <div>
              <label className="form-label">Société / Organisation</label>
              <input type="text" value={form.company} onChange={e => upd('company', e.target.value)}
                className="form-input" placeholder="Nom de votre société" />
            </div>
          </div>

          {/* Identité légale */}
          <div className="card" style={{ marginBottom: 24 }}>
            <h3 style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 20 }}>🏢</span> Identité légale
            </h3>
            {/* SIRET with lookup */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, marginBottom: 16, alignItems: 'end' }}>
              <div>
                <label className="form-label">SIRET</label>
                <input type="text" value={form.siret} onChange={e => upd('siret', e.target.value.replace(/[^0-9]/g, '').substring(0, 14))}
                  className="form-input" placeholder="Numéro SIRET à 14 chiffres" />
              </div>
              <button onClick={handleSiretLookup} className="btn btn-primary" type="button"
                disabled={lookingUp || !(form.siret && form.siret.replace(/[^0-9]/g, '').length >= 9)}
                style={{ padding: '10px 20px', fontSize: 13, whiteSpace: 'nowrap', height: 42 }}>
                {lookingUp ? 'Recherche...' : 'Remplir automatiquement'}
              </button>
            </div>
            {lookupResult && !lookupResult.not_found && (
              <div style={{ padding: '10px 16px', background: '#E8F5E9', borderRadius: 8, marginBottom: 16, fontSize: 13, color: '#2E7D32' }}>
                Données pré-remplies depuis les sources officielles (Pappers / INSEE).
              </div>
            )}
            {lookupResult && lookupResult.not_found && (
              <div style={{ padding: '10px 16px', background: '#FFF3E0', borderRadius: 8, marginBottom: 16, fontSize: 13, color: '#E65100' }}>
                Aucune entreprise trouvée pour ce SIRET. Vérifiez le numéro saisi.
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label className="form-label">SIREN</label>
                <input type="text" value={form.siren} onChange={e => upd('siren', e.target.value)}
                  className="form-input" style={{ background: form.siren ? '#F5F5F5' : 'white' }} />
              </div>
              <div>
                <label className="form-label">Code NAF</label>
                <input type="text" value={form.naf} onChange={e => upd('naf', e.target.value)}
                  className="form-input" style={{ background: form.naf ? '#F5F5F5' : 'white' }} />
              </div>
              <div>
                <label className="form-label">Forme juridique</label>
                <input type="text" value={form.forme_juridique} onChange={e => upd('forme_juridique', e.target.value)}
                  className="form-input" style={{ background: form.forme_juridique ? '#F5F5F5' : 'white' }} />
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label className="form-label">Adresse du siège</label>
              <input type="text" value={form.adresse} onChange={e => upd('adresse', e.target.value)}
                className="form-input" placeholder="Adresse complète" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
              <div>
                <label className="form-label">Dirigeants</label>
                <input type="text" value={form.dirigeants} onChange={e => upd('dirigeants', e.target.value)}
                  className="form-input" placeholder="Noms des dirigeants" />
              </div>
              <div>
                <label className="form-label">Effectif</label>
                <input type="number" value={form.effectif} onChange={e => upd('effectif', e.target.value)}
                  className="form-input" placeholder="Nombre de salariés" />
              </div>
              <div>
                <label className="form-label">Année de création</label>
                <input type="number" value={form.annee_creation} onChange={e => upd('annee_creation', e.target.value)}
                  className="form-input" placeholder="Ex: 2015" />
              </div>
            </div>
          </div>

          {/* Preferences d'investissement */}
          <div className="card" style={{ marginBottom: 24 }}>
            <h3 style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 20 }}>💼</span> Préférences d'investissement
            </h3>
            <div style={{ marginBottom: 16 }}>
              <label className="form-label">Thèse d'investissement</label>
              <textarea value={form.investment_thesis} onChange={e => upd('investment_thesis', e.target.value)}
                className="form-input" rows={4}
                style={{ resize: 'vertical' }}
                placeholder="Décrivez votre stratégie d'investissement, vos critères de sélection..." />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label className="form-label">Secteurs privilégiés</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                  {SECTEUR_OPTIONS.map(s => {
                    const selected = (form.preferred_sectors || '').includes(s);
                    return (
                      <button key={s} type="button"
                        onClick={() => {
                          const current = (form.preferred_sectors || '').split(',').map(x => x.trim()).filter(Boolean);
                          const next = selected ? current.filter(x => x !== s) : [...current, s];
                          upd('preferred_sectors', next.join(', '));
                        }}
                        style={{
                          padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                          border: `2px solid ${selected ? 'var(--primary)' : 'var(--border)'}`,
                          background: selected ? 'var(--primary)' : 'white',
                          color: selected ? 'white' : 'var(--text)',
                          cursor: 'pointer', fontFamily: 'inherit',
                        }}>
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="form-label">Stades de développement visés</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                  {STAGE_OPTIONS.map(s => {
                    const selected = (form.preferred_stages || '').includes(s);
                    return (
                      <button key={s} type="button"
                        onClick={() => {
                          const current = (form.preferred_stages || '').split(',').map(x => x.trim()).filter(Boolean);
                          const next = selected ? current.filter(x => x !== s) : [...current, s];
                          upd('preferred_stages', next.join(', '));
                        }}
                        style={{
                          padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                          border: `2px solid ${selected ? '#39A8E5' : 'var(--border)'}`,
                          background: selected ? '#39A8E5' : 'white',
                          color: selected ? 'white' : 'var(--text)',
                          cursor: 'pointer', fontFamily: 'inherit',
                        }}>
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <div style={{ maxWidth: 300 }}>
              <label className="form-label">Note ISI minimum souhaitée</label>
              <select value={form.min_note} onChange={e => upd('min_note', e.target.value)} className="form-input">
                <option value="">Aucune préférence</option>
                <option value="8">8+ (Excellent)</option>
                <option value="7">7+ (Résilient)</option>
                <option value="5">5+</option>
                <option value="4">4+ (Vigilance)</option>
              </select>
            </div>
          </div>

          {/* Informations complementaires */}
          <div className="card" style={{ marginBottom: 24 }}>
            <h3 style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 20 }}>📋</span> Informations complémentaires
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <label className="form-label">Comment nous avez-vous connu ?</label>
                <select value={form.referral_source} onChange={e => upd('referral_source', e.target.value)} className="form-input">
                  <option value="">-- Non renseigné --</option>
                  <option value="presse">Presse</option>
                  <option value="internet">Site internet</option>
                  <option value="forums_salons">Forums & Salons</option>
                  <option value="demarchage">Démarchage</option>
                  <option value="autre">Autre</option>
                </select>
              </div>
              <div>
                <label className="form-label">Consentement - Visibilité</label>
                <select value={form.consent_visible === false ? 'false' : 'true'} onChange={e => upd('consent_visible', e.target.value === 'true')} className="form-input">
                  <option value="true">Je consens à me rendre visible auprès des startups</option>
                  <option value="false">Je ne consens pas à me rendre visible auprès des startups</option>
                </select>
              </div>
            </div>
          </div>

          {/* Save button */}
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <button type="submit" className="btn btn-primary" disabled={saving}
              style={{ padding: '14px 40px', fontSize: 15 }}>
              {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
            </button>
            {saved && (
              <span style={{ color: '#27AE60', fontWeight: 600, fontSize: 14 }}>
                Profil sauvegardé avec succès
              </span>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
